import { CSSProperties, ReactNode, useEffect, useState } from "react";
import { PropsWithDataTest } from "../../../types";
import { Input } from "./Input";
import { Box } from "../Box";
import { ReactComponent as EditIcon } from "../../../assets/icons/edit_icon.svg";
import { InputProps, SxProps, Typography } from "@mui/material";
import { palette } from "../../../styles/theme";
import { Button } from "../Button";
import { useTranslation } from "react-i18next";
import { EditModal } from "../../../components/EditModal";

interface Props extends PropsWithDataTest {
  name: string;
  onConfirm?: (value: string) => void;
  autoFocus?: boolean;
  defaultValue?: string;
  containerSx?: SxProps;
  valueSx?: SxProps;
  secondaryInput?: ReactNode;
  styles?: CSSProperties;
  maxWidth?: string;
  label?: string;
  inline?: boolean;
  boldLabel?: boolean;
  fullWidth?: boolean;
  labelSx?: CSSProperties;
  editComponent?: ReactNode;
  readOnly?: boolean;
  size?: InputProps["size"] | "extraSmall";
  buttonUnderInput?: boolean;
  useModal?: boolean;
  modalTitle?: string;
}

export const EditableTextInput = ({
  name,
  autoFocus,
  defaultValue,
  styles,
  maxWidth,
  label,
  secondaryInput,
  inline = true,
  boldLabel = false,
  fullWidth = false,
  "data-test": dataTest,
  labelSx,
  containerSx,
  valueSx,
  editComponent,
  onConfirm,
  readOnly = false,
  size = "small",
  buttonUnderInput = false,
  useModal = false,
  modalTitle,
  ...props
}: Props) => {
  const [isEditing, setEditing] = useState(false);
  const [value, setValue] = useState(defaultValue);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const handleEditClick = () => {
    if (useModal) {
      setIsModalOpen(true);
    } else {
      setEditing(true);
    }
  };

  const handleModalSave = (newValue: string) => {
    onConfirm?.(newValue);
    setIsModalOpen(false);
  };

  return (
    <>
      {useModal && (
        <EditModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleModalSave}
          defaultValue={defaultValue || ""}
          title={modalTitle || label || ""}
          label={label}
        />
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: inline
            ? buttonUnderInput && isEditing
              ? "flex-start"
              : "center"
            : "flex-start",
          flexDirection: inline ? "row" : "column",
          gap: "8px",
          maxHeight: inline && !buttonUnderInput ? "34px" : "auto",
          width: fullWidth ? "100%" : "auto",
          ...containerSx,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {editComponent && isEditing ? null : (
            <Typography
              variant={boldLabel ? "textRegularBold" : "textRegular"}
              color={boldLabel ? palette.grey.nearBlack : palette.grey.primary}
              whiteSpace="nowrap"
              sx={labelSx}
            >
              {label}
              {inline ? ":" : ""}
            </Typography>
          )}
          {!inline && !isEditing && !readOnly && (
            <EditIcon
              className="editable-control-icon"
              onClick={handleEditClick}
              style={{ cursor: "pointer" }}
            />
          )}
        </Box>
        {isEditing ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexDirection: buttonUnderInput ? "column" : "row",
            }}
            fullWidth={fullWidth}
          >
            {editComponent ? (
              editComponent
            ) : (
              <Input
                {...props}
                size={size}
                autoFocus={autoFocus}
                style={{ ...styles, maxWidth }}
                data-test={dataTest}
                value={value}
                sx={{
                  height: "40px",
                  ...(size === "extraSmall" && {
                    fontSize: "14px",
                    lineHeight: "15px",
                    height: "34px",
                  }),
                }}
                onChange={(event) => {
                  setValue(event.target.value);
                }}
                onKeyUp={(e) => {
                  if (e.key === "Enter") {
                    onConfirm?.(value || "");
                    setEditing(false);
                  }
                  if (e.key === "Escape") {
                    setEditing(false);
                  }
                }}
              />
            )}
            {secondaryInput}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                height: "100%",
                maxHeight: size === "extraSmall" ? "34px" : "40px",
              }}
            >
              <Button
                size="small"
                variant="contained"
                color="primary"
                sx={{
                  minHeight: "34px",
                  height: size === "extraSmall" ? "34px" : "40px",
                  maxHeight: size === "extraSmall" ? "34px" : "40px",
                  lineHeight: "0",
                }}
                onClick={() => {
                  onConfirm?.(value || "");
                  setEditing(false);
                }}
              >
                {t("general.save")}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                sx={{
                  minHeight: "34px",
                  height: size === "extraSmall" ? "34px" : "40px",
                  maxHeight: size === "extraSmall" ? "34px" : "40px",
                  lineHeight: "0",
                }}
                onClick={() => {
                  setValue(defaultValue || "");
                  setEditing(false);
                }}
              >
                {t("general.cancel")}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              py: inline ? "8px" : "0px",
            }}
          >
            <Typography
              variant="textRegular"
              color={inline ? palette.grey.nearBlack : palette.grey.primary}
              sx={valueSx}
            >
              {defaultValue || "-"}
            </Typography>
            {inline && !readOnly && (
              <EditIcon
                className="editable-control-icon"
                onClick={handleEditClick}
                style={{ cursor: "pointer" }}
              />
            )}
          </Box>
        )}
      </Box>
    </>
  );
};
